import React, { useCallback, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material';

import {
  RecordContextProvider,
  useCreate,
  useGetOne,
  useNotify,
  Form,
  useRecordContext,
  TextInput,
} from 'react-admin';

import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';

import useDialogStatus from '../hooks/useDialogStatus';
import reportTypes from '../constants/reportTypes';
import includeSheetsConstant from '../constants/includeSheets';
import DownloadModalReports from './DownloadModalReports';

const CasePdfReportModal = () => {
  const [reportId, setReportId] = useState();
  const record = useRecordContext();

  const {
    open,
    openDialog,
    closeDialog,
  } = useDialogStatus();

  const [createReport] = useCreate();
  const notify = useNotify();

  const handleClose = useCallback(() => {
    closeDialog();
    setReportId(undefined);
  }, [closeDialog, setReportId]);

  const handleSubmit = useCallback((
    {
      reportName,
    }: {
      reportName?: string;
    },
  ) => {
    createReport(
      'reports/',
      {
        data: {
          type: reportTypes.CASE_PDF,
          report: {
            reportName,
            caseId: record?.id,
          },
        },
      },
      {
        onSuccess: (res) => {
          setReportId(res.id);
        },
        onError: () => {
          notify('Failed to generate entity report', { type: 'error' });
        },
      },
    );
  }, [createReport, record?.id, notify]);

  const { data } = useGetOne(
    'reports',
    { id: reportId },
    {
      enabled: !!reportId,
      refetchInterval: 5000,
    },
  );

  return (
    <>
      <Button
        variant="outlined"
        onClick={openDialog}
        startIcon={<DescriptionIcon />}
      >
        Case report
      </Button>
      <Dialog onClose={handleClose} open={open} maxWidth="sm" fullWidth>
        <Form
          onSubmit={handleSubmit}
          record={{
            includeSheets: Object.values(includeSheetsConstant),
            currencies: [],
          }}
        >
          <Box padding={4}>
            <Box display="flex" justifyContent="space-between">
              <DialogTitle sx={{ padding: 0 }} fontWeight="bold">Generate case report</DialogTitle>
              <IconButton size="medium" onClick={handleClose} sx={{ padding: 0 }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <DialogContentText>
              You can choose the report name.
            </DialogContentText>
          </Box>
          <DialogContent sx={{ py: 2, px: 4 }}>
            <TextInput fullWidth source="reportName" />
            {reportId && (
              <DialogContentText margin={2}>
                Your report will be generated.
                You can close this dialog and access the report later from Reports.
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: 4 }}>
            {!reportId && (<Button onClick={handleClose} sx={{ margin: 2 }}>Cancel</Button>)}
            <RecordContextProvider value={data}>
              <DownloadModalReports reportType={reportTypes.CASE_PDF} />
            </RecordContextProvider>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
};

export default CasePdfReportModal;
